/**
 * Error Boundary Component
 * Catches React errors and displays error page
 */

import { Component, type ReactNode } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { AlertCircle, Home, RefreshCcw } from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  handleGoHome = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = '/dashboard';
  };

  override render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4">
          <Card className="w-full max-w-md">
            <CardContent className="pt-6">
              <div className="flex flex-col items-center justify-center text-center py-8">
                <div className="flex h-20 w-20 items-center justify-center rounded-full bg-destructive/10 mb-6">
                  <AlertCircle className="h-10 w-10 text-destructive" />
                </div>

                <h1 className="scroll-m-20 text-4xl/tight font-bold tracking-tight mb-2">
                  Oops!
                </h1>

                <h2 className="scroll-m-20 text-xl/snug font-semibold tracking-tight mb-4">
                  Something went wrong
                </h2>

                <p className="text-sm/5 text-muted-foreground mb-6 max-w-sm">
                  {this.state.error?.message ||
                    'An unexpected error occurred. Please try reloading the page.'}
                </p>

                {process.env.NODE_ENV !== "production" && this.state.error?.stack && (
                  <Alert variant="destructive" className="mb-6 text-left">
                    <AlertDescription className="font-mono text-xs overflow-auto max-h-40">
                      {this.state.error.stack}
                    </AlertDescription>
                  </Alert>
                )}

                <div className="flex flex-col gap-2 w-full sm:flex-row sm:justify-center">
                  <Button onClick={this.handleReload} className="w-full sm:w-auto">
                    <RefreshCcw className="mr-2 h-4 w-4" />
                    Reload Page
                  </Button>
                  <Button
                    onClick={this.handleGoHome}
                    variant="outline"
                    className="w-full sm:w-auto"
                  >
                    <Home className="mr-2 h-4 w-4" />
                    Go to Dashboard
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
